import React, { Component } from "react";
import {
  Card,
  CardBody,
  Col,
  Row,
  Button,
  Container,
  FormGroup,
  Label,
  Input,
  Progress,
  Badge,
} from "reactstrap";
import {
  getBannerById,
  createBanner,
  updateBanner,
  getCouponList,
  getBannerList,
} from "../../Endpoint/Admin/index";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { ToastContainer, toast } from "react-toastify";

class BannerForm extends Component {
  constructor(props) {
    super(props);
    const bannerId = this.props.match?.params?.id;

    this.state = {
      bannerId: bannerId || null,
      isEditMode: !!bannerId,
      isLoading: bannerId ? true : false,
      currentStep: 1,
      totalSteps: 3,

      // Dropdown data
      coupons: [],

      // Step 1: Basic Info + Image
      title: "",
      description: "",
      bannerImage: null,
      imagePreview: null,
      existingImageUrl: null,

      // Step 2: Link Settings
      linkType: "none",
      linkedCouponId: "",
      externalUrl: "",
      internalRoute: "",

      // Step 3: Schedule & Display
      displayOrder: 0,
      startDate: "",
      endDate: "",
      isActive: true,

      // Validation helpers
      usedDisplayOrders: [],
      bannerOrderMap: {}, // Map of bannerId -> displayOrder for edit mode validation
      displayOrderError: "",

      // Banner layout options
      layoutType: "split", // 'split' = text left/image right, 'fullImage' = just image
      ctaText: "View Now",
      bgTheme: "red", // red, blue, green, orange, purple, dark

      breadcrumbItems: [
        { title: "Dashboard", link: "/dashboard" },
        { title: "Banners", link: "/banner" },
        { title: bannerId ? "Edit Banner" : "Create Banner", link: "#" },
      ],
    };

    this.fileInputRef = React.createRef();
  }

  componentDidMount() {
    this.loadCoupons();
    this.loadExistingBanners();
    if (this.state.bannerId) {
      this.loadBanner();
    }
  }

  loadExistingBanners = () => {
    getBannerList({})
      .then((result) => {
        if (result.status === 200) {
          const banners = result.data || [];
          const usedDisplayOrders = banners.map(b => b.displayOrder);
          // Create a map of bannerId -> displayOrder for edit validation
          const bannerOrderMap = {};
          banners.forEach(b => {
            bannerOrderMap[b._id] = b.displayOrder;
          });

          // For new banners, suggest the next available order (unique)
          if (!this.state.bannerId) {
            const maxOrder = usedDisplayOrders.length > 0 ? Math.max(...usedDisplayOrders) : -1;
            this.setState({
              usedDisplayOrders,
              bannerOrderMap,
              displayOrder: maxOrder + 1,
            });
          } else {
            this.setState({ usedDisplayOrders, bannerOrderMap });
          }
        }
      })
      .catch(() => console.log("Error loading banners for display order"));
  };

  loadCoupons = () => {
    getCouponList({ status: "active" })
      .then((result) => {
        if (result.status === 200) {
          this.setState({ coupons: result.data || [] });
        }
      })
      .catch(() => console.log("Error loading coupons"));
  };

  loadBanner = () => {
    getBannerById(this.state.bannerId)
      .then((result) => {
        if (result.status === 200) {
          const banner = result.data;
          this.setState({
            isLoading: false,
            title: banner.title || "",
            description: banner.description || "",
            existingImageUrl: banner.imageUrl || null,
            linkType: banner.linkType || "none",
            linkedCouponId: banner.linkedCouponId?._id || banner.linkedCouponId || "",
            externalUrl: banner.externalUrl || "",
            internalRoute: banner.internalRoute || "",
            displayOrder: banner.displayOrder || 0,
            startDate: banner.startDate ? banner.startDate.split("T")[0] : "",
            endDate: banner.endDate ? banner.endDate.split("T")[0] : "",
            isActive: banner.isActive !== false,
            layoutType: banner.layoutType || "split",
            ctaText: banner.ctaText || "View Now",
            bgTheme: banner.bgTheme || "red",
          });
        } else {
          toast.error(result.message || "Failed to load banner");
          this.props.history.push("/banner");
        }
      })
      .catch(() => {
        toast.error("Error loading banner");
        this.props.history.push("/banner");
      });
  };

  handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === "checkbox" ? checked : value;

    // Date validation
    if (name === "startDate" && value) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const selectedDate = new Date(value);

      // For new banners, don't allow past dates
      if (!this.state.bannerId && selectedDate < today) {
        toast.error("Start date cannot be in the past");
        return;
      }

      // If end date exists and is before new start date, clear end date
      if (this.state.endDate && new Date(this.state.endDate) < selectedDate) {
        this.setState({ endDate: "" });
        toast.warning("End date was cleared because it was before the new start date");
      }
    }

    if (name === "endDate" && value) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const selectedDate = new Date(value);
      const startDate = this.state.startDate ? new Date(this.state.startDate) : today;

      // End date cannot be before start date (or today if no start date)
      if (selectedDate < startDate) {
        toast.error("End date cannot be before start date");
        return;
      }

      // End date cannot be in the past
      if (selectedDate < today) {
        toast.error("End date cannot be in the past");
        return;
      }
    }

    this.setState({
      [name]: newValue,
    }, () => {
      // Check for display order conflicts
      if (name === "displayOrder") {
        this.checkDisplayOrderConflict(parseInt(newValue));
      }
    });
  };

  checkDisplayOrderConflict = (orderValue) => {
    const { bannerOrderMap, bannerId } = this.state;

    // Check if this order is used by another banner
    let isOrderTaken = false;
    for (const [bId, bOrder] of Object.entries(bannerOrderMap)) {
      if (bOrder === orderValue && bId !== bannerId) {
        isOrderTaken = true;
        break;
      }
    }

    this.setState({
      displayOrderError: isOrderTaken ? `Display order #${orderValue} is already taken. Choose a unique number.` : "",
    });
  };

  handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const validTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
      if (!validTypes.includes(file.type)) {
        toast.error("Please select a valid image (JPEG, PNG, or WebP)");
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Image size must be less than 5MB");
        return;
      }

      this.setState({
        bannerImage: file,
        imagePreview: URL.createObjectURL(file),
      });
    }
  };

  removeImage = () => {
    if (this.state.imagePreview) {
      URL.revokeObjectURL(this.state.imagePreview);
    }
    this.setState({
      bannerImage: null,
      imagePreview: null,
      existingImageUrl: null,
    });
    if (this.fileInputRef.current) {
      this.fileInputRef.current.value = "";
    }
  };

  nextStep = () => {
    const { currentStep, totalSteps } = this.state;
    if (this.validateCurrentStep()) {
      if (currentStep < totalSteps) {
        this.setState({ currentStep: currentStep + 1 });
      }
    }
  };

  prevStep = () => {
    const { currentStep } = this.state;
    if (currentStep > 1) {
      this.setState({ currentStep: currentStep - 1 });
    }
  };

  validateCurrentStep = () => {
    const {
      currentStep,
      title,
      bannerImage,
      existingImageUrl,
      bannerId,
      linkType,
      linkedCouponId,
      externalUrl,
      internalRoute,
      displayOrder,
      usedDisplayOrders,
    } = this.state;

    switch (currentStep) {
      case 1:
        if (!title.trim()) {
          toast.error("Please enter a banner title");
          return false;
        }
        if (!bannerId && !bannerImage && !existingImageUrl) {
          toast.error("Please upload a banner image");
          return false;
        }
        return true;

      case 2:
        // Link type validation - if a type is selected, the corresponding field is required
        if (linkType === "coupon" && !linkedCouponId) {
          toast.error("Please select a coupon or change link type to 'No Action'");
          return false;
        }
        if (linkType === "external" && !externalUrl.trim()) {
          toast.error("Please enter an external URL or change link type to 'No Action'");
          return false;
        }
        if (linkType === "internal" && !internalRoute) {
          toast.error("Please select an app route or change link type to 'No Action'");
          return false;
        }
        return true;

      case 3:
        // Display order must be unique
        const orderNum = parseInt(displayOrder);
        const { bannerOrderMap } = this.state;

        // Check if this order is used by another banner
        let isOrderTaken = false;
        for (const [bId, bOrder] of Object.entries(bannerOrderMap)) {
          if (bOrder === orderNum && bId !== bannerId) {
            isOrderTaken = true;
            break;
          }
        }

        if (isOrderTaken) {
          toast.error(`Display order #${orderNum} is already used by another banner. Please choose a unique number.`);
          return false;
        }
        return true;

      default:
        return true;
    }
  };

  handleSubmit = () => {
    if (!this.validateCurrentStep()) return;

    const {
      bannerId,
      title,
      description,
      bannerImage,
      linkType,
      linkedCouponId,
      externalUrl,
      internalRoute,
      displayOrder,
      startDate,
      endDate,
      isActive,
      layoutType,
      ctaText,
      bgTheme,
    } = this.state;

    // Date validations
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (startDate) {
      const start = new Date(startDate);
      // Don't allow past start dates for new banners
      if (!bannerId && start < today) {
        toast.error("Start date cannot be in the past");
        return;
      }
    }

    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      if (end < start) {
        toast.error("End date must be after start date");
        return;
      }
    }

    // Create form data for file upload
    const formData = new FormData();
    formData.append("title", title);
    formData.append("description", description);
    formData.append("linkType", linkType);
    formData.append("placement", "home");
    formData.append("displayOrder", displayOrder.toString());
    formData.append("isActive", isActive.toString());
    formData.append("layoutType", layoutType);
    formData.append("ctaText", ctaText);
    formData.append("bgTheme", bgTheme);

    if (bannerImage) {
      formData.append("bannerImage", bannerImage);
    }

    if (linkType === "coupon" && linkedCouponId) {
      formData.append("linkedCouponId", linkedCouponId);
    }
    if (linkType === "external" && externalUrl) {
      formData.append("externalUrl", externalUrl);
    }
    if (linkType === "internal" && internalRoute) {
      formData.append("internalRoute", internalRoute);
    }
    if (startDate) {
      formData.append("startDate", startDate);
    }
    if (endDate) {
      formData.append("endDate", endDate);
    }

    const apiCall = bannerId ? updateBanner(bannerId, formData) : createBanner(formData);

    apiCall
      .then((response) => {
        if (response && response.status === 200) {
          toast.success(bannerId ? "Banner updated!" : "Banner created!");
          setTimeout(() => this.props.history.push("/banner"), 1500);
        } else {
          toast.error(response?.message || "Failed to save banner");
        }
      })
      .catch((err) => {
        console.error(err);
        toast.error("Error saving banner");
      });
  };

  getLinkedCouponName = () => {
    const { linkedCouponId, coupons } = this.state;
    if (!linkedCouponId) return null;
    const coupon = coupons.find(c => c._id === linkedCouponId);
    return coupon ? `${coupon.code} - ${coupon.discountValue}${coupon.discountType === 'percentage' ? '%' : '$'}` : null;
  };

  renderBannerPreview = () => {
    const {
      title,
      description,
      imagePreview,
      existingImageUrl,
      linkType,
      startDate,
      endDate,
      isActive,
    } = this.state;

    // Get image URL - for server images, prepend API base URL
    const serverImageUrl = existingImageUrl && !existingImageUrl.startsWith('blob:') && !existingImageUrl.startsWith('http')
      ? `${process.env.REACT_APP_DEFAULTAUTH}${existingImageUrl}`
      : existingImageUrl;
    const currentImage = imagePreview || serverImageUrl;

    return (
      <div className="banner-preview-container">
        {/* Banner Image Preview */}
        <div
          className="banner-image-preview position-relative rounded-lg overflow-hidden"
          style={{
            background: currentImage ? 'transparent' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            minHeight: '160px',
          }}
        >
          {currentImage ? (
            <img
              src={currentImage}
              alt="Banner Preview"
              className="w-100"
              style={{ height: '160px', objectFit: 'cover' }}
            />
          ) : (
            <div className="d-flex align-items-center justify-content-center h-100" style={{ minHeight: '160px' }}>
              <div className="text-center text-white">
                <i className="ri-image-line" style={{ fontSize: '48px', opacity: 0.7 }}></i>
                <p className="mb-0 mt-2">No image uploaded</p>
              </div>
            </div>
          )}

          {/* Status Badge */}
          <Badge
            color={isActive ? "success" : "secondary"}
            className="position-absolute"
            style={{ top: '10px', right: '10px' }}
          >
            {isActive ? "Active" : "Inactive"}
          </Badge>

          {/* Link Type Badge */}
          {linkType !== "none" && (
            <Badge
              color="info"
              className="position-absolute"
              style={{ top: '10px', left: '10px' }}
            >
              <i className={`mr-1 ${
                linkType === 'coupon' ? 'ri-ticket-line' :
                linkType === 'external' ? 'ri-external-link-line' :
                'ri-link'
              }`}></i>
              {linkType === 'coupon' ? 'Coupon' : linkType === 'external' ? 'External' : 'Internal'}
            </Badge>
          )}
        </div>

        {/* Banner Info */}
        <div className="mt-3 p-3 bg-light rounded">
          <h6 className="mb-2 font-weight-bold">{title || "Banner Title"}</h6>
          {description && (
            <p className="text-muted small mb-3">{description}</p>
          )}

          <div className="border-top pt-3">
            <div className="d-flex justify-content-between mb-2">
              <span className="text-muted small">Link Type:</span>
              <span className="font-weight-medium small">
                {linkType === 'none' && 'No Link'}
                {linkType === 'coupon' && (this.getLinkedCouponName() || 'Coupon')}
                {linkType === 'external' && 'External URL'}
                {linkType === 'internal' && 'App Route'}
              </span>
            </div>

            <div className="d-flex justify-content-between mb-2">
              <span className="text-muted small">Schedule:</span>
              <span className="font-weight-medium small">
                {startDate || 'Now'} - {endDate || 'No End'}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  renderStep1 = () => {
    const { title, description, imagePreview, existingImageUrl, layoutType, ctaText, bgTheme, linkType } = this.state;

    // Get image URL for display
    const serverImageUrl = existingImageUrl && !existingImageUrl.startsWith('blob:') && !existingImageUrl.startsWith('http')
      ? `${process.env.REACT_APP_DEFAULTAUTH}${existingImageUrl}`
      : existingImageUrl;
    const currentImage = imagePreview || serverImageUrl;

    return (
      <div>
        <h5 className="mb-4">
          <span className="badge badge-primary mr-2">1</span>
          Basic Information
        </h5>

        <FormGroup>
          <Label>Banner Title *</Label>
          <Input
            type="text"
            name="title"
            value={title}
            onChange={this.handleChange}
            placeholder="e.g., Summer Sale - 20% Off"
          />
          <small className="text-muted">A catchy title for your banner</small>
        </FormGroup>

        <FormGroup>
          <Label>Description</Label>
          <Input
            type="textarea"
            name="description"
            value={description}
            onChange={this.handleChange}
            rows={2}
            placeholder="Brief description (optional)..."
          />
        </FormGroup>

        {/* Layout Type Selection */}
        <FormGroup className="mt-4">
          <Label>Banner Layout *</Label>
          <div className="d-flex">
            <div
              className={`flex-grow-1 p-3 mr-2 rounded text-center border ${layoutType === "split" ? "border-primary bg-soft-primary" : "border-light"}`}
              style={{ cursor: "pointer" }}
              onClick={() => this.setState({ layoutType: "split" })}
            >
              <div className="d-flex align-items-center justify-content-center mb-2" style={{ height: "50px" }}>
                <div className="d-flex border rounded overflow-hidden" style={{ width: "80px", height: "40px" }}>
                  <div className="bg-danger" style={{ width: "50%" }}></div>
                  <div className="bg-light" style={{ width: "50%" }}></div>
                </div>
              </div>
              <span className={`font-weight-medium ${layoutType === "split" ? "text-primary" : ""}`}>Split Layout</span>
              <small className="d-block text-muted">Text left, Image right</small>
            </div>
            <div
              className={`flex-grow-1 p-3 rounded text-center border ${layoutType === "fullImage" ? "border-primary bg-soft-primary" : "border-light"}`}
              style={{ cursor: "pointer" }}
              onClick={() => this.setState({ layoutType: "fullImage" })}
            >
              <div className="d-flex align-items-center justify-content-center mb-2" style={{ height: "50px" }}>
                <div className="border rounded bg-light d-flex align-items-center justify-content-center" style={{ width: "80px", height: "40px" }}>
                  <i className="ri-image-line text-muted"></i>
                </div>
              </div>
              <span className={`font-weight-medium ${layoutType === "fullImage" ? "text-primary" : ""}`}>Full Image</span>
              <small className="d-block text-muted">Pre-designed banner</small>
            </div>
          </div>
        </FormGroup>

        {/* Split Layout Options */}
        {layoutType === "split" && (
          <div className="p-3 bg-light rounded mb-3">
            <Row>
              <Col md={6}>
                <FormGroup className="mb-2">
                  <Label className="small">Button Text</Label>
                  <Input
                    type="text"
                    name="ctaText"
                    value={ctaText}
                    onChange={this.handleChange}
                    placeholder="View Now"
                    disabled={linkType === "none"}
                  />
                  {linkType === "none" && <small className="text-muted">Set a link type in Step 2 to show button</small>}
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup className="mb-2">
                  <Label className="small">Background Color</Label>
                  <div className="d-flex flex-wrap">
                    {[
                      { id: "red", color: "#E53935" },
                      { id: "blue", color: "#1E88E5" },
                      { id: "green", color: "#43A047" },
                      { id: "orange", color: "#FF9800" },
                      { id: "purple", color: "#8E24AA" },
                      { id: "dark", color: "#37474F" },
                    ].map((theme) => (
                      <div
                        key={theme.id}
                        className={`mr-2 mb-1 rounded-circle ${bgTheme === theme.id ? "border border-dark" : ""}`}
                        style={{
                          width: "28px",
                          height: "28px",
                          backgroundColor: theme.color,
                          cursor: "pointer",
                          boxShadow: bgTheme === theme.id ? "0 0 0 2px #fff, 0 0 0 4px " + theme.color : "none",
                        }}
                        onClick={() => this.setState({ bgTheme: theme.id })}
                        title={theme.id.charAt(0).toUpperCase() + theme.id.slice(1)}
                      />
                    ))}
                  </div>
                </FormGroup>
              </Col>
            </Row>
          </div>
        )}

        {layoutType === "fullImage" && (
          <div className="alert alert-info mb-3">
            <i className="ri-information-line mr-1"></i>
            Upload a pre-designed banner image. The image will be displayed as-is.
          </div>
        )}

        <FormGroup>
          <Label>Banner Image *</Label>
          {currentImage ? (
            <div className="mb-3">
              <div className="position-relative">
                <img
                  src={currentImage}
                  alt="Banner Preview"
                  className="w-100 rounded"
                  style={{ height: '150px', objectFit: 'cover' }}
                />
                <Button
                  color="danger"
                  size="sm"
                  className="position-absolute"
                  style={{ top: '8px', right: '8px' }}
                  onClick={this.removeImage}
                  title="Remove image"
                >
                  <i className="ri-close-line"></i>
                </Button>
              </div>
              <div className="d-flex mt-2">
                <Button
                  color="outline-primary"
                  size="sm"
                  className="flex-grow-1"
                  onClick={() => this.fileInputRef.current?.click()}
                >
                  <i className="ri-refresh-line mr-1"></i> Replace Image
                </Button>
              </div>
            </div>
          ) : (
            <div
              className="border rounded p-4 text-center bg-light"
              style={{ cursor: 'pointer' }}
              onClick={() => this.fileInputRef.current?.click()}
            >
              <i className="ri-image-add-line d-block mb-2" style={{ fontSize: '40px', color: 'var(--text-muted)' }}></i>
              <span className="text-muted">Click to upload banner image</span>
              <br />
              <small className="text-muted">Recommended: 1200x400px, Max 5MB</small>
            </div>
          )}
          <input
            type="file"
            ref={this.fileInputRef}
            accept="image/jpeg,image/jpg,image/png,image/webp"
            onChange={this.handleImageChange}
            style={{ display: 'none' }}
          />
          {!currentImage && (
            <Button
              color="outline-primary"
              className="mt-2 w-100"
              onClick={() => this.fileInputRef.current?.click()}
            >
              <i className="ri-upload-line mr-1"></i> Choose Image
            </Button>
          )}
        </FormGroup>
      </div>
    );
  };

  renderStep2 = () => {
    const { linkType, linkedCouponId, externalUrl, internalRoute, coupons } = this.state;

    return (
      <div>
        <h5 className="mb-4">
          <span className="badge badge-primary mr-2">2</span>
          Link Settings
        </h5>

        <FormGroup>
          <Label>What happens when banner is clicked?</Label>
          <div className="d-flex flex-wrap">
            {[
              { id: "none", icon: "ri-cursor-line", label: "No Action", desc: "Just display" },
              { id: "coupon", icon: "ri-ticket-line", label: "Show Coupon", desc: "Apply coupon" },
              { id: "external", icon: "ri-external-link-line", label: "Open URL", desc: "External link" },
              { id: "internal", icon: "ri-smartphone-line", label: "App Route", desc: "Navigate in app" },
            ].map((type) => (
              <div
                key={type.id}
                className={`p-3 mr-2 mb-2 rounded text-center border flex-grow-1 ${
                  linkType === type.id ? "border-primary bg-soft-primary" : "border-light"
                }`}
                style={{ cursor: "pointer", minWidth: "100px" }}
                onClick={() => this.setState({ linkType: type.id })}
              >
                <i className={`${type.icon} d-block mb-1 ${linkType === type.id ? "text-primary" : "text-muted"}`} style={{ fontSize: "20px" }}></i>
                <span className="font-weight-medium d-block" style={{ fontSize: "13px" }}>{type.label}</span>
                <small className="text-muted">{type.desc}</small>
              </div>
            ))}
          </div>
        </FormGroup>

        {linkType === "coupon" && (
          <Card className={`border mb-3 ${!linkedCouponId ? "border-danger" : "border-info"}`}>
            <CardBody>
              <h6 className="text-info mb-3">
                <i className="ri-ticket-line mr-1"></i> Select Coupon <span className="text-danger">*</span>
              </h6>
              <FormGroup className="mb-0">
                <Input
                  type="select"
                  name="linkedCouponId"
                  value={linkedCouponId}
                  onChange={this.handleChange}
                  className={!linkedCouponId ? "border-danger" : ""}
                >
                  <option value="">-- Select a coupon (required) --</option>
                  {coupons.map((coupon) => (
                    <option key={coupon._id} value={coupon._id}>
                      {coupon.code} - {coupon.name} (
                      {coupon.discountType === "percentage"
                        ? `${coupon.discountValue}%`
                        : `$${coupon.discountValue}`}
                      )
                    </option>
                  ))}
                </Input>
                {!linkedCouponId && (
                  <small className="text-danger d-block mt-1">
                    <i className="ri-error-warning-line mr-1"></i>
                    Please select a coupon or change to "No Action"
                  </small>
                )}
                <small className="text-muted">
                  When clicked, this coupon will be shown to the user
                </small>
              </FormGroup>
            </CardBody>
          </Card>
        )}

        {linkType === "external" && (
          <Card className={`border mb-3 ${!externalUrl.trim() ? "border-danger" : "border-warning"}`}>
            <CardBody>
              <h6 className="text-warning mb-3">
                <i className="ri-external-link-line mr-1"></i> External URL <span className="text-danger">*</span>
              </h6>
              <FormGroup className="mb-0">
                <Input
                  type="url"
                  name="externalUrl"
                  value={externalUrl}
                  onChange={this.handleChange}
                  placeholder="https://example.com"
                  className={!externalUrl.trim() ? "border-danger" : ""}
                />
                {!externalUrl.trim() && (
                  <small className="text-danger d-block mt-1">
                    <i className="ri-error-warning-line mr-1"></i>
                    Please enter a URL or change to "No Action"
                  </small>
                )}
                <small className="text-muted">
                  Opens in a new browser tab when banner is clicked
                </small>
              </FormGroup>
            </CardBody>
          </Card>
        )}

        {linkType === "internal" && (
          <Card className={`border mb-3 ${!internalRoute ? "border-danger" : "border-success"}`}>
            <CardBody>
              <h6 className="text-success mb-3">
                <i className="ri-smartphone-line mr-1"></i> App Route <span className="text-danger">*</span>
              </h6>
              <FormGroup className="mb-0">
                <Input
                  type="select"
                  name="internalRoute"
                  value={internalRoute}
                  onChange={this.handleChange}
                  className={!internalRoute ? "border-danger" : ""}
                >
                  <option value="">-- Select app route (required) --</option>
                  <option value="/food">Food & Dining</option>
                  <option value="/rooms">Rooms & Booking</option>
                  <option value="/amenities">Amenities</option>
                  <option value="/attractions">Attractions</option>
                  <option value="/transport">Transportation</option>
                  <option value="/coupons">Coupons & Offers</option>
                </Input>
                {!internalRoute && (
                  <small className="text-danger d-block mt-1">
                    <i className="ri-error-warning-line mr-1"></i>
                    Please select an app route or change to "No Action"
                  </small>
                )}
                <small className="text-muted">
                  Select where users will be redirected when they tap the banner
                </small>
              </FormGroup>
            </CardBody>
          </Card>
        )}

        {linkType === "none" && (
          <div className="text-center p-4 bg-light rounded">
            <i className="ri-cursor-line text-muted" style={{ fontSize: "40px" }}></i>
            <p className="mb-0 mt-2 text-muted">
              Banner will be displayed without any click action
            </p>
          </div>
        )}
      </div>
    );
  };

  renderStep3 = () => {
    const { displayOrder, startDate, endDate, isActive, displayOrderError, bannerId, usedDisplayOrders } = this.state;

    // Get today's date for min attribute
    const today = new Date().toISOString().split('T')[0];

    // Calculate max order for display
    const maxOrder = usedDisplayOrders.length > 0 ? Math.max(...usedDisplayOrders) : 0;
    const suggestedOrder = maxOrder + 1;

    return (
      <div>
        <h5 className="mb-4">
          <span className="badge badge-primary mr-2">3</span>
          Schedule & Display
        </h5>

        <FormGroup>
          <Label>Display Order <span className="text-danger">*</span></Label>
          <div className="d-flex align-items-center">
            <Input
              type="number"
              name="displayOrder"
              value={displayOrder}
              onChange={this.handleChange}
              min="0"
              className={displayOrderError ? "border-danger" : ""}
              style={{ maxWidth: "120px" }}
            />
            {!bannerId && (
              <Badge color="success" className="ml-2 p-2">
                <i className="ri-lightbulb-line mr-1"></i>
                Suggested: #{suggestedOrder}
              </Badge>
            )}
          </div>
          <small className="text-muted d-block mt-1">
            Lower number = Higher priority. Must be unique. Current highest: <strong>#{maxOrder}</strong>
          </small>
          {displayOrderError && (
            <div className="text-danger small mt-1">
              <i className="ri-error-warning-line mr-1"></i>
              {displayOrderError}
            </div>
          )}
          {usedDisplayOrders.length > 0 && (
            <div className="mt-2 p-2 bg-light rounded">
              <small className="text-muted d-block mb-1">Orders already in use (choose different):</small>
              <div className="d-flex flex-wrap">
                {[...new Set(usedDisplayOrders)].sort((a, b) => a - b).map((order) => (
                  <Badge
                    key={order}
                    color={order === parseInt(displayOrder) ? "danger" : "secondary"}
                    className="mr-1 mb-1"
                    style={{ fontSize: "11px" }}
                  >
                    #{order}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </FormGroup>

        <Row>
          <Col md={6}>
            <FormGroup>
              <Label>Start Date {!bannerId && <span className="text-muted small">(min: today)</span>}</Label>
              <Input
                type="date"
                name="startDate"
                value={startDate}
                onChange={this.handleChange}
                min={!bannerId ? today : undefined}
              />
              <small className="text-muted">Leave empty for immediate start</small>
            </FormGroup>
          </Col>
          <Col md={6}>
            <FormGroup>
              <Label>End Date {startDate && <span className="text-muted small">(must be after start)</span>}</Label>
              <Input
                type="date"
                name="endDate"
                value={endDate}
                onChange={this.handleChange}
                min={startDate || today}
              />
              <small className="text-muted">Leave empty for no expiry</small>
            </FormGroup>
          </Col>
        </Row>

        {/* Date Summary */}
        {(startDate || endDate) && (
          <div className="alert alert-light border mt-2 mb-3 p-2">
            <i className="ri-calendar-check-line mr-2 text-primary"></i>
            <strong>Schedule:</strong>{" "}
            {startDate ? new Date(startDate).toLocaleDateString() : "Immediately"}
            {" → "}
            {endDate ? new Date(endDate).toLocaleDateString() : "No end date"}
          </div>
        )}

        <FormGroup className="mt-3">
          <div className="custom-control custom-switch">
            <input
              type="checkbox"
              className="custom-control-input"
              id="isActive"
              name="isActive"
              checked={isActive}
              onChange={this.handleChange}
            />
            <label className="custom-control-label" htmlFor="isActive">
              {isActive ? (
                <span className="text-success font-weight-bold">Active - Banner is visible</span>
              ) : (
                <span className="text-muted">Inactive - Banner is hidden</span>
              )}
            </label>
          </div>
        </FormGroup>

        <div className="alert alert-info mt-4 mb-0">
          <i className="ri-information-line mr-2"></i>
          <strong>Tip:</strong> Banners will be displayed on the Home/Dashboard screen in the mobile app.
        </div>
      </div>
    );
  };

  render() {
    const { isEditMode, isLoading, currentStep, totalSteps } = this.state;

    if (isLoading) {
      return (
        <div className="page-content">
          <Container fluid>
            <div className="text-center py-5">
              <i className="ri-loader-4-line ri-2x spin"></i>
              <p className="mt-2">Loading banner...</p>
            </div>
          </Container>
        </div>
      );
    }

    const stepTitles = ["Basic Info", "Link Settings", "Schedule"];

    return (
      <React.Fragment>
        <div className="page-content">
          <Container fluid>
            <Breadcrumbs
              title={isEditMode ? "Edit Banner" : "Create Banner"}
              breadcrumbItems={this.state.breadcrumbItems}
            />

            <Row>
              {/* Form Section */}
              <Col lg={7}>
                <Card>
                  <CardBody>
                    {/* Progress Bar */}
                    <div className="mb-4">
                      <div className="d-flex justify-content-between mb-2">
                        {stepTitles.map((title, idx) => (
                          <span
                            key={idx}
                            className={`text-${currentStep > idx ? "success" : currentStep === idx + 1 ? "primary" : "muted"}`}
                            style={{ fontSize: "12px", fontWeight: currentStep === idx + 1 ? "600" : "400" }}
                          >
                            {title}
                          </span>
                        ))}
                      </div>
                      <Progress
                        value={(currentStep / totalSteps) * 100}
                        color="primary"
                        style={{ height: "6px" }}
                      />
                    </div>

                    {/* Step Content */}
                    <div className="step-content" style={{ minHeight: "300px" }}>
                      {currentStep === 1 && this.renderStep1()}
                      {currentStep === 2 && this.renderStep2()}
                      {currentStep === 3 && this.renderStep3()}
                    </div>

                    {/* Navigation */}
                    <div className="d-flex justify-content-between mt-4 pt-3 border-top">
                      <Button
                        color="light"
                        onClick={currentStep === 1 ? () => this.props.history.push("/banner") : this.prevStep}
                      >
                        <i className="ri-arrow-left-line mr-1"></i>
                        {currentStep === 1 ? "Cancel" : "Back"}
                      </Button>

                      {currentStep < totalSteps ? (
                        <Button color="primary" onClick={this.nextStep}>
                          Next <i className="ri-arrow-right-line ml-1"></i>
                        </Button>
                      ) : (
                        <Button color="success" onClick={this.handleSubmit}>
                          <i className={`${isEditMode ? "ri-save-line" : "ri-check-line"} mr-1`}></i>
                          {isEditMode ? "Update Banner" : "Create Banner"}
                        </Button>
                      )}
                    </div>
                  </CardBody>
                </Card>
              </Col>

              {/* Preview Section */}
              <Col lg={5}>
                <div className="sticky-top" style={{ top: "80px" }}>
                  <Card>
                    <CardBody>
                      <h5 className="card-title mb-4">
                        <i className="ri-eye-line mr-2"></i>
                        Live Preview
                      </h5>
                      {this.renderBannerPreview()}
                    </CardBody>
                  </Card>
                </div>
              </Col>
            </Row>
          </Container>
        </div>
        <ToastContainer />
      </React.Fragment>
    );
  }
}

export default BannerForm;
